import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import useBodyScrollLock from "hooks/useBodyScrollLock";
import { heldTypes } from "constants/permissions";
import { SPACE_FEATURE_ENABLED } from "constants/featureFlags";
import { nativeHasCapability } from "common/nativeRoute";
import { openActiveTypeSheet } from "common/nativeBridge";
import { openMemberType } from "util/openMemberType";
import ActivityPulseDot from "components/ui/ActivityPulseDot";

/**
 * ★ 2026-10-31 하단 MY 탭 = 내 유형(인스타 프로필 탭처럼).
 *  - 탭 글씨: 지금 유형(강사·일반·원장·무용수·공연/기획)
 *  - 길게 누르기: 유형 바텀시트(가진 유형 + 일반, 지금 유형 체크, 맨 아래 '유형 편집'(추가·해제))
 *    앱(activeTypeSheet 지원)은 네이티브 바텀시트, 웹·구버전 앱은 이 웹 시트.
 *  - 두 번 누르기: 고를 수 있는 유형이 딱 2개면 반대 유형으로 바로 전환.
 *  전환은 AuthContext.setActiveType 한 곳 — 서버 저장·앱 동기화·"○○ 유형으로 전환됐어요" 안내가 함께 된다.
 */
const GENERAL = "GENERAL";

/** 탭 글씨(짧게). */
export const TYPE_TAB_LABEL = { TEACHER: "강사", GENERAL: "일반", ACADEMY: "원장", DANCER: "무용수", TEAM: "공연/기획", SPACE: "일반" };
/** 시트 줄 이름. */
export const TYPE_SHEET_LABEL = { TEACHER: "강사", GENERAL: "일반", ACADEMY: "학원/원장", DANCER: "무용수", TEAM: "공연/기획" };

/** 지금 유형(취미·꺼진 공간 유형은 일반으로 본다). */
export const visibleType = (activeType) =>
  !activeType || activeType === "HOBBY" || (!SPACE_FEATURE_ENABLED && activeType === "SPACE") ? GENERAL : activeType;

/** 고를 수 있는 유형 = 일반 + 가진 전문 유형(AuthContext.setActiveType 이 허용하는 범위와 같다). */
export const switchableTypes = (user) => [
  GENERAL,
  ...heldTypes(user).filter((t) => t !== "HOBBY" && t !== GENERAL && (SPACE_FEATURE_ENABLED || t !== "SPACE")),
];

/** 시트 열기 — 앱이면 네이티브 시트(true), 아니면 false(호출부가 웹 시트를 연다). */
export const openNativeTypeSheet = (types, current, newTypes = []) => {
  if (!nativeHasCapability("activeTypeSheet")) return false;
  return openActiveTypeSheet({
    types: types.map((t) => ({ type: t, label: TYPE_SHEET_LABEL[t] || t })),
    current,
    newTypes,
  });
};

export default function ActiveTypeSheet({ isShow, onClose, newTypes = [] }) {
  const navigate = useNavigate();
  const { user, activeType, setActiveType } = useAuth();
  useBodyScrollLock(isShow);
  const current = visibleType(activeType);
  const types = switchableTypes(user);

  const pick = (type) => {
    onClose();
    if (type === "MANAGE") { openMemberType(navigate, current); return; }
    if (type && type !== current) setActiveType(type);
  };

  // 네이티브 시트가 고른 값(type | "MANAGE") — 탭바는 늘 떠 있으므로 여기서 받는다.
  useEffect(() => {
    window.__onActiveTypePicked = (type) => pick(String(type || ""));
    return () => { delete window.__onActiveTypePicked; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  // 인스타그램 계정 전환 시트와 같은 구성 — 손잡이, 둥근 카드 한 장에 줄(동그란 프로필·이름·체크), 맨 아래 '유형 편집'.
  return (
    <div data-id="active-type-sheet" className={"modal-bottom-dimmed type-sheet" + (isShow ? " is-show" : "")} onClick={onClose}>
      <div className="modal-bottom" role="dialog" aria-modal="true" aria-label="내 유형" onClick={(e) => e.stopPropagation()}>
        <span className="type-sheet-grabber" aria-hidden="true" />
        <div className="type-sheet-card">
          {types.map((t) => (
            <button key={t} type="button" className="type-sheet-row" aria-current={t === current ? "true" : undefined}
              onClick={() => pick(t)}>
              <span className={"type-sheet-avatar" + (t === current ? " is-current" : "")} aria-hidden="true">
                {(TYPE_SHEET_LABEL[t] || t).slice(0, 1)}
              </span>
              <span className="type-sheet-name">
                {TYPE_SHEET_LABEL[t] || t}
                {t !== current && newTypes.includes(t) && <ActivityPulseDot label="새 소식 있음" />}
              </span>
              {t === current && (
                <span className="type-sheet-check" aria-label="지금 유형">
                  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                    <path d="M3 7.2l2.6 2.6L11 4.4" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              )}
            </button>
          ))}
          {/* 유형 편집 — 회원유형 화면에서 추가·해제(다 가졌어도 해제하러 갈 수 있게 늘 둔다). */}
          <button type="button" className="type-sheet-row" onClick={() => pick("MANAGE")}>
            <span className="type-sheet-avatar is-add" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12.5 3.5l4 4L7 17H3v-4l9.5-9.5z" /><path d="M10.5 5.5l4 4" />
              </svg>
            </span>
            <span className="type-sheet-name">유형 편집</span>
          </button>
        </div>
      </div>
    </div>
  );
}
